export const FIELD_TEAM_PATH = '/field-team-management';
export const FIELD_TEAM_ALIAS_PATH = '/field-operations';
export const CRM_LOGIN_PATH = '/crm-login';
export const DOWNLOAD_PATH = '/download';
export const CONTACT_PATH = '/crm-contact';
export const DEMO_PATH = '/crm-contact?intent=demo';

export const HERO_SECTION_ID = 'ftl-hero';
export const LOGIN_SECTION_ID = 'login';
export const SOLUTIONS_SECTION_ID = 'solutions';
export const FEATURES_SECTION_ID = 'features';
export const INDUSTRIES_SECTION_ID = 'industries';
export const PRODUCT_SECTION_ID = 'product';
export const HOW_SECTION_ID = 'how-it-works';
export const PRICING_SECTION_ID = 'pricing';

export const FIELD_TEAM_SOLUTION_SLUGS = [
  'sales',
  'technician',
  'security',
  'supervisor',
  'cleaning',
  'workforce',
  'crm',
] as const;

export type FieldTeamSolutionSlug = (typeof FIELD_TEAM_SOLUTION_SLUGS)[number];

/** Dedicated SEO routes from the landing-funnel spec. */
export const SOLUTION_PATHS: Record<FieldTeamSolutionSlug, string> = {
  sales: '/sales-team-tracking',
  technician: '/field-service-management',
  security: '/security-workforce-management',
  supervisor: '/supervisor-management',
  cleaning: '/cleaning-workforce-management',
  workforce: '/field-workforce-management',
  crm: '/crm-lead-management',
};

/** Cards in the “What Type of Team Do You Manage?” funnel. */
export const FUNNEL_TEAM_SLUGS: FieldTeamSolutionSlug[] = [
  'sales',
  'technician',
  'security',
  'supervisor',
  'cleaning',
  'workforce',
];

export function isFieldTeamSolutionSlug(value: string | undefined): value is FieldTeamSolutionSlug {
  return !!value && (FIELD_TEAM_SOLUTION_SLUGS as readonly string[]).includes(value);
}

export function isFieldTeamHomePath(pathname: string): boolean {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return clean === FIELD_TEAM_PATH || clean === CRM_LOGIN_PATH || clean === FIELD_TEAM_ALIAS_PATH;
}

export function fieldTeamHomePath(currentPath?: string): string {
  if (currentPath && isFieldTeamHomePath(currentPath)) return currentPath.replace(/\/+$/, '') || FIELD_TEAM_PATH;
  return FIELD_TEAM_PATH;
}

export function fieldTeamSolutionPath(slug: FieldTeamSolutionSlug): string {
  return SOLUTION_PATHS[slug];
}

export function fieldTeamLandingUrl(currentPath?: string): string {
  return fieldTeamHomePath(currentPath);
}

export function fieldTeamSectionUrl(sectionId: string, currentPath?: string): string {
  if (sectionId === HERO_SECTION_ID || sectionId === 'top') {
    return fieldTeamLandingUrl(currentPath);
  }
  return `${fieldTeamHomePath(currentPath)}#${sectionId}`;
}

export function crmLoginSectionUrl(sectionId: string): string {
  return fieldTeamSectionUrl(sectionId);
}

export function solutionSlugFromPath(pathname: string): FieldTeamSolutionSlug | null {
  const clean = pathname.replace(/\/+$/, '');
  for (const slug of FIELD_TEAM_SOLUTION_SLUGS) {
    if (SOLUTION_PATHS[slug] === clean) return slug;
  }
  const legacy = clean.match(/^\/crm-login\/([a-z-]+)$/);
  if (legacy && isFieldTeamSolutionSlug(legacy[1])) return legacy[1];
  return null;
}

export function scrollToFieldTeamSection(sectionId: string, behavior: ScrollBehavior = 'smooth') {
  if (sectionId === HERO_SECTION_ID || sectionId === 'top' || !sectionId) {
    window.scrollTo({ top: 0, behavior });
    return;
  }
  document.getElementById(sectionId)?.scrollIntoView({ behavior, block: 'start' });
}

export const FIELD_TEAM_MARKETING_PATHS = [
  FIELD_TEAM_PATH,
  CONTACT_PATH,
  ...Object.values(SOLUTION_PATHS),
];

export function isFieldTeamMarketingPath(pathname: string): boolean {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return isFieldTeamHomePath(clean) || clean === CONTACT_PATH || solutionSlugFromPath(clean) !== null;
}
